import { useEffect, useState } from 'react';
import InAppRoutes, { pushProgressive } from 'utils/router';
import Card from 'components/atoms/Card';
import Button from 'components/atoms/Button';
import Typography from 'components/atoms/Typography';
import TemplateForm from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm';

import { toast } from 'react-toastify';
import { useThunkDispatch } from 'utils/reduxHook';
import { getTemplate, updateTemplate } from 'actions/requests';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  DiscoverItemType,
  DownloadTemplateConfig,
  LinkTemplateConfig,
  Templates,
  TemplateType,
} from 'utils/gloo-types';
import DiscoverCardPreview from 'pages/_AdminAuthoringPages/_components/DiscoverCardPreview';
import TemplatePreviewCard from 'components/organisms/TemplatePreviewCard';
import { capitalize } from 'lodash';
import { discoverItemCardForTemplate } from 'utils/discover';
import DiscoverItemArchiveToggleButton from 'pages/_AdminAuthoringPages/_components/DiscoverItemArchiveToggleButton';
import classNames from 'classnames';

function AdminTemplateEdit() {
  const dispatch = useThunkDispatch();
  const templateId = useParams<{ templateId: string }>().templateId;
  const [template, setTemplate] = useState<Templates>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setIsLoading(true);
        const existingTemplate = await dispatch(getTemplate(templateId));

        setTemplate(existingTemplate);
      } finally {
        setIsLoading(false);
      }
    };
    loadTemplate();
  }, []);

  const handleTemplateSave = async () => {
    setIsSaving(true);
    try {
      const updatedTemplate = await dispatch(updateTemplate(template.id, template));
      if (!updatedTemplate.error) toast.success('Your changes have been saved.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <article className="container-1">
      <Card border className="flex items-center justify-between p-4 m-2">
        <Button
          dataDurableId="TemplateBackButton"
          type="secondary"
          icon="arrow-left"
          onClick={() => {
            if (!location.key) {
              pushProgressive(InAppRoutes.AdminAuthoringHome);
            } else {
              history.goBack();
            }
          }}
          text="Back"
          className={classNames('mr-4')}
        />
        <Typography className="text-thryveGreenDark flex-grow" variant="h1">
          Edit {capitalize(template?.type)} Template
        </Typography>
        <div className="flex flex-row gap-4">
          {template && (
            <DiscoverItemArchiveToggleButton
              archivedAt={template?.archivedAt}
              descriptor={{ type: DiscoverItemType.TEMPLATE, id: template?.id }}
              onArchivedAtChange={(archivedAt) => {
                setTemplate({
                  ...template,
                  archivedAt,
                  visibleInDiscover: archivedAt ? false : template.visibleInDiscover,
                });
              }}
            />
          )}
          <Button
            icon="save"
            iconPosition="left"
            buttonType="submit"
            dataDurableId="adminTemplateEditSaveButton"
            form="templateEditForm"
            type="primary"
            text="SAVE"
            disabled={isSaving || (!!template && !!template.archivedAt)}
            noBubble
          />
        </div>
      </Card>

      {!isLoading && (
        <section className="grid grid-cols-4">
          <Card border className="p-4 m-2 col-span-3">
            <TemplateForm
              formId="templateEditForm"
              template={template}
              handleChange={setTemplate}
              handleSubmit={handleTemplateSave}
            />
          </Card>
          <figure className="col-span-1 p-4">
            <Typography className="text-thryveGray my-4" variant="h3">
              DISCOVER CARD PREVIEW:
            </Typography>
            {template && (
              <DiscoverCardPreview
                className="w-80"
                itemCard={discoverItemCardForTemplate(template)}
                onClick={() => {
                  if (template.type === TemplateType.LINK) {
                    const config = template.config as LinkTemplateConfig;
                    window.open(config.url);
                  } else if (template.type === TemplateType.DOWNLOAD) {
                    const config = template.config as DownloadTemplateConfig;
                    window.open(config.url);
                  }
                }}
              />
            )}
            <Card border className="mt-10">
              <Typography className="text-thryveGray my-4" variant="h3">
                DRAWER HEADER PREVIEW:
              </Typography>
              {template && <TemplatePreviewCard template={template} />}
            </Card>
          </figure>
        </section>
      )}
    </article>
  );
}

export default AdminTemplateEdit;
